import styled from "styled-components";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

export const TableContainer = styled.div`
  background: ${({ theme }) => theme.card.bg.primary || "#ffffff"};
  /* Rounded corners and minimal shadow */
  border-radius: 12px;
  box-shadow: 0 1px 2px 0 rgba(10, 13, 18, 0.05);

  .table > :not(caption) > * > * {
    background-color: transparent;
  }

  /* Apply 12px radius to all tables within this container */
  .table {
    overflow: hidden;
  }

  /* Allow sticky header to escape the table's clipping box */
  .table-wrapper {
    overflow: visible;
  }

  thead {
    position: sticky; // relative to the scroll wrapper
    top: 0;
    z-index: 10;
    background: ${({ theme }) => theme.table.bg.header || "#f9fafb"};
    /* Ensure sticky works consistently across browsers */
    display: table-header-group;
  }

  /* In some browsers, setting sticky on th improves consistency */
  thead .table-row th {
    position: sticky; // relative to the scroll wrapper
    top: 0;
    z-index: 10;
    background: ${({ theme }) => theme.table.bg.header || "#f9fafb"};
  }

  /* Ensure table headers follow requested typography */
  thead .table-header,
  thead .table-header .header-content {
    color: #717680;
    font-family: "Geist", sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px;
  }

  .table-wrapper {
    border: none;
    width: 100%;
    margin-bottom: 0;

    .table-row {
      background: ${({ theme }) => theme.table.bg.header || "#f9fafb"};
      border-bottom: 1px solid
        ${({ theme }) => theme.divider.bg.primary || "#e5e7eb"};

      th,
      td {
        padding: 16px 18px;
        font-size: 13px;
        font-weight: 400;
        border: none;
        text-align: left;
        border-bottom: 1px solid
          ${({ theme }) => theme.divider.bg.primary || "#e5e7eb"};

        &:first-child {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }

        &:last-child {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
      }
    }

    &.table > tbody {
      vertical-align: middle;
      width: 100%;
      tr {
        border-bottom: 1px solid
          ${({ theme }) => theme.divider.bg.primary || "#e5e7eb"};
        height: 60px;
        cursor: pointer;

        &:last-child {
          border-bottom: none !important;

          td {
            border-bottom: none !important;
          }
        }

        &:hover {
          background-color: ${({ theme }) => theme.table.bg.hover || "#f9fafb"};
        }
      }

      tr td.table-cell:last-child {
        text-align: left;
      }
    }

    .table-cell {
      text-align: left;
      padding: 16px 18px;
      font-size: 14px;
      font-weight: 400;
      vertical-align: middle;
    }

    .no-data-text {
      color: "#535862";
      text-align: center;
      border: none;
      padding: 48px 24px;
      font-size: 14px;
      font-weight: 400;

      &:hover {
        background-color: transparent;
      }
    }

    .search-row {
      height: 42px !important;

      .no-search-container {
        display: flex;
        align-items: center;
        gap: 5px;
        background-color: white;
        border: none;
        padding: 9px 16px;

        input {
          width: 100%;
          padding: 2px 10px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
          background: white;
        }
      }
    }

    .data-count-text {
      color: ${({ theme }) => theme.table.font.secondary || "#6b7280"};
      margin: 0;
      font-size: 14px;
      font-weight: 400;
    }

    .table-pagination {
      border: none;
      background: ${({ theme }) => theme.card.bg.primary || "#ffffff"};
      border-top: 1px solid
        ${({ theme }) => theme.divider.bg.primary || "#e5e7eb"};
      padding: 16px 24px;

      .record-counter-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .page-button-wrapper {
          display: flex;
          gap: 4px;
        }
      }

      .pagination-btn {
        background-color: ${({ theme }) => theme.card.bg.primary || "#ffffff"};
        border: 1px solid
          ${({ theme }) => theme.divider.bg.primary || "#e5e7eb"};
        color: ${({ theme }) => theme.table.font.primary || "#374151"};
        font-size: 14px;
        font-weight: 500;
        border-radius: 6px;
        padding: 8px 12px;
        transition: all 0.2s ease;

        &:hover {
          background-color: ${({ theme }) => theme.table.bg.hover || "#f9fafb"};
          border-color: ${({ theme }) =>
            theme.divider.bg.secondary || "#d1d5db"};
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;

          &:hover {
            background-color: ${({ theme }) =>
              theme.card.bg.primary || "#ffffff"};
          }
        }
      }

      .button-small {
        padding: 8px 12px;
        height: auto;
        min-height: 36px;
      }

      .next {
        border-top-left-radius: 6px;
        border-bottom-left-radius: 6px;
      }

      .prev {
        border-top-right-radius: 6px;
        border-bottom-right-radius: 6px;
      }

      .inactive-page {
        background-color: ${({ theme }) => theme.card.bg.primary || "#ffffff"};
        color: ${({ theme }) => theme.table.font.secondary || "#6b7280"};
      }
    }
  }

  .table-scroll-wrapper {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 12px;
    position: relative; /* anchor for sticky header */
  }

  .top-table-pagination {
    border: none;
    padding: 16px 24px;
    background: ${({ theme }) => theme.card.bg.primary || "#ffffff"};
    border-bottom: 1px solid
      ${({ theme }) => theme.divider.bg.primary || "#e5e7eb"};

    .record-counter-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .data-count-text {
      font-size: 14px;
      font-weight: 400;
      margin: 0;
      color: ${({ theme }) => theme.table.font.secondary || "#6b7280"};
    }

    .pagination-btn {
      background-color: transparent;
      border: none;
      font-size: 14px;
      font-weight: 500;
      border-radius: 6px;
      padding: 8px;
      color: ${({ theme }) => theme.table.font.secondary || "#6b7280"};
      transition: all 0.2s ease;

      &:hover {
        background-color: ${({ theme }) => theme.table.bg.hover || "#f9fafb"};
        color: ${({ theme }) => theme.table.font.primary || "#374151"};
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;

        &:hover {
          background-color: transparent;
          color: ${({ theme }) => theme.table.font.secondary || "#6b7280"};
        }
      }
    }

    button {
      width: fit-content !important;
      padding: 8px 12px;
      background-color: ${({ theme }) => theme.card.bg.primary || "#ffffff"};
      border: 1px solid ${({ theme }) => theme.divider.bg.primary || "#e5e7eb"};
      color: ${({ theme }) => theme.table.font.primary || "#374151"};
      font-size: 14px;
      font-weight: 500;
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        background-color: ${({ theme }) => theme.table.bg.hover || "#f9fafb"};
        border-color: ${({ theme }) => theme.divider.bg.secondary || "#d1d5db"};
        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
      }
    }

    .prev,
    .next {
      cursor: pointer;
    }

    .inactive-page {
      background-color: transparent;
      border: none;
      color: ${({ theme }) => theme.table.font.secondary || "#6b7280"};
    }
  }

  /* Mobile responsive classes */
  &.mobile-responsive {
    .table-wrapper {
      /* Add mobile-specific styling indicators */
      position: relative;

      &::before {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 20px;
        background: linear-gradient(to left, rgba(0, 0, 0, 0.1), transparent);
        pointer-events: none;
        z-index: 2;
      }
    }
  }

  /* Mobile stacking layout */
  &.mobile-stacking {
    @media (max-width: 768px) {
      .table-wrapper {
        min-width: auto;

        thead {
          display: none;
        }

        tbody {
          tr {
            display: block;
            border: 1px solid
              ${({ theme }) => theme.divider.bg.primary || "#e5e7eb"};
            border-radius: 8px;
            margin-bottom: 12px;
            padding: 16px;
            background: ${({ theme }) => theme.card.bg.primary || "#ffffff"};
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);

            &:hover {
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }

            td {
              display: block;
              border: none;
              padding: 8px 0;
              text-align: left;

              &:before {
                content: attr(data-label) ": ";
                font-weight: 600;
                color: ${({ theme }) =>
                  theme.table.font.secondary || "#6b7280"};
                display: inline-block;
                min-width: 100px;
                margin-right: 8px;
              }

              &:first-child {
                border-top: none;
                padding-top: 0;
              }

              &:last-child {
                border-bottom: none;
                padding-bottom: 0;
              }
            }
          }
        }
      }
    }
  }

  /* Mobile Responsive Design */
  @media (max-width: 1250px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    .table-scroll-wrapper {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .table-wrapper {
      min-width: 900px;

      thead {
        position: sticky;
        top: 0;
        z-index: 10;
        background: ${({ theme }) => theme.table.bg.header || "#f9fafb"};

        .table-row {
          display: table-row;
          width: 100%;
        }
      }

      .table-row {
        th,
        td {
          padding: 12px 8px;
          font-size: 12px;
          white-space: nowrap;
          display: table-cell;
          vertical-align: middle;

          /* Reduce padding for action columns */
          &.action-column {
            padding: 8px 4px;
            min-width: 80px;
          }
        }
      }

      .table-cell {
        padding: 12px 8px;
        font-size: 13px;

        /* Ensure text doesn't break awkwardly */
        word-break: break-word;
        hyphens: auto;
      }
    }

    /* Pagination adjustments */
    .table-pagination {
      .record-counter-wrapper {
        align-items: flex-start;
        flex-direction: column;
        gap: 12px;

        .page-button-wrapper {
          width: 100%;
          justify-content: center;
          flex-wrap: wrap;
        }

        .data-count-text {
          font-size: 12px;
          text-align: center;
          width: 100%;
        }
      }

      .pagination-btn {
        padding: 6px 8px;
        font-size: 12px;
        min-width: 32px;

        &.prev,
        &.next {
          min-width: 60px;
        }
      }
    }

    /* Top pagination adjustments */
    .top-table-pagination {
      .record-counter-wrapper {
        flex-direction: column;
        align-items: center;
        gap: 12px;

        .data-count-text {
          font-size: 12px;
        }
      }

      .pagination-btn {
        padding: 6px;

        svg {
          width: 16px;
          height: 16px;
        }
      }

      button {
        padding: 6px 8px;
        font-size: 12px;
        min-width: 32px;
      }
    }
  }

  /* Tablet responsive design */
  @media (max-width: 1024px) and (min-width: 769px) {
    .table-wrapper {
      .table-row {
        th,
        td {
          padding: 14px 12px;
          font-size: 13px;
        }
      }

      .table-cell {
        padding: 14px 12px;
        font-size: 13px;
      }
    }

    .table-pagination {
      .record-counter-wrapper {
        .page-button-wrapper {
          gap: 2px;
        }
      }

      .pagination-btn {
        padding: 6px 10px;
        font-size: 13px;
      }
    }
  }

  /* Small mobile devices */
  @media (max-width: 480px) {
    .table-wrapper {
      min-width: 500px;

      .table-row {
        th,
        td {
          padding: 10px 6px;
          font-size: 11px;

          &:first-child {
            padding-left: 12px;
          }

          &:last-child {
            padding-right: 12px;
          }
        }
      }

      .table-cell {
        padding: 10px 6px;
        font-size: 12px;

        &:first-child {
          padding-left: 12px;
        }

        &:last-child {
          padding-right: 12px;
        }
      }
    }

    .table-pagination {
      padding: 12px 16px;

      .record-counter-wrapper {
        .page-button-wrapper {
          gap: 1px;
        }
      }

      .pagination-btn {
        padding: 4px 6px;
        font-size: 11px;
        min-width: 28px;

        &.prev,
        &.next {
          min-width: 50px;
        }
      }
    }

    .top-table-pagination {
      padding: 12px 16px;

      .pagination-btn {
        padding: 4px;

        svg {
          width: 14px;
          height: 14px;
        }
      }

      button {
        padding: 4px 6px;
        font-size: 11px;
        min-width: 28px;
      }
    }
  }
`;
export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 6px;
`;

export const StyledArrowUp = styled(ArrowUp).attrs({
  size: 14,
  color: "#111827",
  strokeWidth: 2.5,
})``;

export const StyledArrowDown = styled(ArrowDown).attrs({
  size: 14,
  color: "#111827",
  strokeWidth: 2.5,
})``;

export const StyledArrowUpDown = styled(ArrowUpDown).attrs({
  size: 14,
  color: "#9ca3af",
  strokeWidth: 2,
})``;
